import React, { useState } from 'react';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import LoginApi from '../../services/LoginApiClient';
import { clientSurfaceCardClass } from '../../utils/clientChromeClasses';

export default function ChangePasswordSection() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null as string | null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await LoginApi.changePassword(currentPassword, newPassword);
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update password.');
    } finally {
      setSubmitting(false);
    }
  };

  const ring = 'focus:ring-2 focus:ring-emerald-600/40 focus:border-emerald-600';

  return (
    <div className={`${clientSurfaceCardClass} p-6 sm:p-8`}>
      <div className="flex items-start gap-3 mb-6">
        <KeyRound className="text-emerald-700 shrink-0 mt-0.5" size={22} aria-hidden />
        <div>
          <h2 className="text-lg font-medium text-gray-900 tracking-tight">Change Password</h2>
          <p className="text-[15px] leading-snug text-gray-600 mt-1.5">
            Enter your current password, then choose a new one (at least 8 characters).
          </p>
        </div>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4 max-w-md">
        {success ? (
          <div
            className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
            role="status"
          >
            Password updated successfully.
          </div>
        ) : null}
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900" role="alert">
            {error}
          </div>
        ) : null}

        <div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <label htmlFor="change-pw-current" className="block text-sm font-medium text-gray-700">
              Current password
            </label>
            <button
              type="button"
              onClick={() => setShowCurrent((v) => !v)}
              className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900 focus:outline-none focus:underline"
            >
              {showCurrent ? <EyeOff size={14} aria-hidden /> : <Eye size={14} aria-hidden />}
              {showCurrent ? 'Hide' : 'Show'}
            </button>
          </div>
          <input
            id="change-pw-current"
            name="currentPassword"
            type={showCurrent ? 'text' : 'password'}
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className={`w-full h-10 px-3 text-sm border border-gray-300 rounded-lg outline-none ${ring}`}
          />
        </div>

        <div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <label htmlFor="change-pw-new" className="block text-sm font-medium text-gray-700">
              New password
            </label>
            <button
              type="button"
              onClick={() => setShowNew((v) => !v)}
              className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900 focus:outline-none focus:underline"
            >
              {showNew ? <EyeOff size={14} aria-hidden /> : <Eye size={14} aria-hidden />}
              {showNew ? 'Hide' : 'Show'}
            </button>
          </div>
          <input
            id="change-pw-new"
            name="newPassword"
            type={showNew ? 'text' : 'password'}
            autoComplete="new-password"
            required
            minLength={8}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="At least 8 characters"
            className={`w-full h-10 px-3 text-sm border border-gray-300 rounded-lg outline-none ${ring}`}
          />
        </div>

        <div>
          <label htmlFor="change-pw-confirm" className="block text-sm font-medium text-gray-700 mb-1">
            Confirm new password
          </label>
          <input
            id="change-pw-confirm"
            name="confirmPassword"
            type={showNew ? 'text' : 'password'}
            autoComplete="new-password"
            required
            minLength={8}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={`w-full h-10 px-3 text-sm border border-gray-300 rounded-lg outline-none ${ring}`}
          />
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-lg btn-primary text-sm font-medium focus:ring-2 focus:ring-emerald-600/50 focus:ring-offset-2 disabled:opacity-50"
          >
            {submitting ? 'Updating…' : 'Update password'}
          </button>
        </div>
      </form>
    </div>
  );
}
